import React, {useRef, useCallback, useState, useMemo} from 'react';
import styled from 'styled-components';
import {Editor, EditorState, RichUtils, ContentBlock, ContentState} from 'draft-js';
import type {DraftHandleValue} from 'draft-js';
import 'draft-js/dist/Draft.css';
import {EditorToolbar} from './EditorToolbar';
import {MentionSuggestions} from './MentionSuggestions';
import {useMentionUsers} from '../hooks/useMentionUsers';
import {findMentionTrigger} from '../utils/mentionStrategy';
import {insertMention} from '../utils/insertMention';
import type {MentionUser} from '../hooks/useMentionUsers';

const EditorShell = styled.div`
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const EditorSurface = styled.div`
	position: relative;
	min-height: 120px;
	padding: 12px 16px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #1e293b;
`;

const InlineImage = styled.img`
	margin: 8px 0;
	max-height: 256px;
	max-width: 100%;
	border-radius: 4px;
`;

interface Props {
	editorState: EditorState;
	onChange: (state: EditorState) => void;
	placeholder?: string;
	mentionEnabled?: boolean;
}

export function RichTextEditor({editorState, onChange, placeholder, mentionEnabled}: Props) {
	const editorRef = useRef<Editor>(null);
	const editorContainerRef = useRef<HTMLDivElement>(null);

	const {users: mentionUsers} = useMentionUsers();
	const [mentionQuery, setMentionQuery] = useState<string | null>(null);
	const [mentionTriggerStart, setMentionTriggerStart] = useState<number>(0);
	const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
	const [mentionPosition, setMentionPosition] = useState({top: 0, left: 0});

	const mentionOpen = mentionEnabled === true && mentionQuery !== null;

	const filteredMentionUsers = useMemo(() => {
		if (mentionQuery === null) return [];
		if (!mentionQuery.trim()) return mentionUsers;
		const lower = mentionQuery.toLowerCase();
		return mentionUsers.filter(u => u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));
	}, [mentionUsers, mentionQuery]);

	const updateMentionPosition = useCallback(() => {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0 || !editorContainerRef.current) return;
		const range = sel.getRangeAt(0);
		const rect = range.getBoundingClientRect();
		const containerRect = editorContainerRef.current.getBoundingClientRect();
		setMentionPosition({
			top: rect.bottom - containerRect.top + 4,
			left: rect.left - containerRect.left,
		});
	}, []);

	const handleToggleInlineStyle = useCallback(
		(style: string) => {
			onChange(RichUtils.toggleInlineStyle(editorState, style));
		},
		[editorState, onChange]
	);

	const handleToggleBlockType = useCallback(
		(blockType: string) => {
			onChange(RichUtils.toggleBlockType(editorState, blockType));
		},
		[editorState, onChange]
	);

	const handleInsertLink = useCallback(() => {
		const selection = editorState.getSelection();
		if (selection.isCollapsed()) return;

		const url = window.prompt('Enter URL:');
		if (!url) return;

		const lower = url.trim().toLowerCase();
		if (lower.startsWith('javascript:')) return;

		const contentState = editorState.getCurrentContent();
		const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {url});
		const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
		const newState = RichUtils.toggleLink(
			EditorState.set(editorState, {currentContent: contentStateWithEntity}),
			selection,
			entityKey
		);
		onChange(newState);
	}, [editorState, onChange]);

	const handleKeyCommand = useCallback(
		(command: string, state: EditorState): DraftHandleValue => {
			const newState = RichUtils.handleKeyCommand(state, command);
			if (newState) {
				onChange(newState);
				return 'handled';
			}
			return 'not-handled';
		},
		[onChange]
	);

	const handleMentionSelect = useCallback(
		(user: MentionUser) => {
			const newState = insertMention(editorState, {
				id: user.id,
				name: user.name,
				triggerStart: mentionTriggerStart,
			});
			onChange(newState);
			setMentionQuery(null);
		},
		[editorState, onChange, mentionTriggerStart]
	);

	const handleEditorChange = useCallback(
		(newState: EditorState) => {
			onChange(newState);

			if (mentionEnabled !== true) return;

			const selection = newState.getSelection();
			if (!selection.isCollapsed()) {
				setMentionQuery(null);
				return;
			}

			const content = newState.getCurrentContent();
			const block = content.getBlockForKey(selection.getAnchorKey());
			const cursorOffset = selection.getAnchorOffset();
			const trigger = findMentionTrigger(block, cursorOffset);

			if (trigger) {
				setMentionQuery(trigger.query);
				setMentionTriggerStart(trigger.start);
				setMentionActiveIndex(0);
				setTimeout(updateMentionPosition, 0);
			} else {
				setMentionQuery(null);
			}
		},
		[onChange, mentionEnabled, updateMentionPosition]
	);

	const handleReturn = useCallback(
		(e: React.KeyboardEvent): DraftHandleValue => {
			if (mentionOpen && filteredMentionUsers.length > 0) {
				e.preventDefault();
				handleMentionSelect(filteredMentionUsers[mentionActiveIndex]!);
				return 'handled';
			}
			return 'not-handled';
		},
		[mentionOpen, filteredMentionUsers, mentionActiveIndex, handleMentionSelect]
	);

	const handleUpArrow = useCallback(
		(e: React.KeyboardEvent) => {
			if (mentionOpen) {
				e.preventDefault();
				setMentionActiveIndex(prev => (prev <= 0 ? filteredMentionUsers.length - 1 : prev - 1));
			}
		},
		[mentionOpen, filteredMentionUsers.length]
	);

	const handleDownArrow = useCallback(
		(e: React.KeyboardEvent) => {
			if (mentionOpen) {
				e.preventDefault();
				setMentionActiveIndex(prev => (prev >= filteredMentionUsers.length - 1 ? 0 : prev + 1));
			}
		},
		[mentionOpen, filteredMentionUsers.length]
	);

	const handleEscape = useCallback(
		(e: React.KeyboardEvent) => {
			if (mentionOpen) {
				e.preventDefault();
				setMentionQuery(null);
			}
		},
		[mentionOpen]
	);

	const currentInlineStyle = editorState.getCurrentInlineStyle();
	const activeStyles = ['BOLD', 'ITALIC', 'UNDERLINE'].filter(s => currentInlineStyle.has(s));
	const selection = editorState.getSelection();
	const currentContent = editorState.getCurrentContent();
	const activeBlockType = currentContent.getBlockForKey(selection.getStartKey()).getType();

	const blockRendererFn = (block: ContentBlock) => {
		if (block.getType() === 'atomic') {
			return {
				component: ImageBlock,
				editable: false,
			};
		}
		return null;
	};

	return (
		<EditorShell>
			<EditorToolbar
				onToggleInlineStyle={handleToggleInlineStyle}
				onToggleBlockType={handleToggleBlockType}
				onInsertLink={handleInsertLink}
				activeStyles={activeStyles}
				activeBlockType={activeBlockType}
			/>
			<EditorSurface ref={editorContainerRef} onClick={() => editorRef.current?.focus()}>
				<Editor
					ref={editorRef}
					editorState={editorState}
					onChange={handleEditorChange}
					placeholder={placeholder}
					handleKeyCommand={handleKeyCommand}
					handleReturn={handleReturn}
					onDownArrow={handleDownArrow}
					onUpArrow={handleUpArrow}
					onEscape={handleEscape}
					blockRendererFn={blockRendererFn}
				/>
				{mentionOpen && (
					<MentionSuggestions
						users={mentionUsers}
						query={mentionQuery ?? ''}
						activeIndex={mentionActiveIndex}
						onSelect={handleMentionSelect}
						position={mentionPosition}
					/>
				)}
			</EditorSurface>
		</EditorShell>
	);
}

interface ImageBlockProps {
	contentState: ContentState;
	block: ContentBlock;
}

function ImageBlock({contentState, block}: ImageBlockProps) {
	const entity = contentState.getEntity(block.getEntityAt(0));
	const {src} = entity.getData() as {src: string};
	return <InlineImage src={src} alt="Inline image" />;
}
